import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Save, ImagePlus, Loader2, Monitor, Tablet, Smartphone } from "lucide-react";

export default function AddBannerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    isActive: true,
    sortOrder: 1,
    fileDesktop: null,
    previewDesktop: "",
    existingPathDesktop: "",
    fileTablet: null,
    previewTablet: "",
    existingPathTablet: "",
    fileMobile: null,
    previewMobile: "",
    existingPathMobile: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get(`/banner/${id}`);
        const d = res.data;
        const base = process.env.REACT_APP_UPLOAD_URL;
        const desktop = d.imagePathPc || "";
        const tablet = d.imagePathTablet || "";
        const mobile = d.imagePathMobile || "";
        setFormData((p) => ({
          ...p,
          name: d.name || "",
          isActive: d.isActive,
          sortOrder: d.sortOrder || 1,
          previewDesktop: desktop ? `${base}/${desktop}` : "",
          existingPathDesktop: desktop,
          previewTablet: tablet ? `${base}/${tablet}` : "",
          existingPathTablet: tablet,
          previewMobile: mobile ? `${base}/${mobile}` : "",
          existingPathMobile: mobile,
        }));
      } catch {
        toast.error("Banner yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (fileKey, previewKey, file) => {
    if (!file) return;
    setFormData((p) => ({ ...p, [fileKey]: file }));
    const reader = new FileReader();
    reader.onload = (ev) =>
      setFormData((p) => ({ ...p, [previewKey]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const existingKeyMap = {
    fileDesktop: "existingPathDesktop",
    fileTablet: "existingPathTablet",
    fileMobile: "existingPathMobile",
  };

  const clearFile = (fileKey, previewKey) => {
    setFormData((p) => ({
      ...p,
      [fileKey]: null,
      [previewKey]: "",
      [existingKeyMap[fileKey]]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error("Banner adı gerekli");
    setSaving(true);

    const data = new FormData();
    data.append("Name", formData.name);
    data.append("IsActive", formData.isActive);
    data.append("SortOrder", formData.sortOrder);
    if (formData.fileDesktop) data.append("ImageFilePc", formData.fileDesktop);
    else if (formData.existingPathDesktop) data.append("ImagePathPc", formData.existingPathDesktop);
    if (formData.fileTablet) data.append("ImageFileTablet", formData.fileTablet);
    else if (formData.existingPathTablet) data.append("ImagePathTablet", formData.existingPathTablet);
    if (formData.fileMobile) data.append("ImageFileMobile", formData.fileMobile);
    else if (formData.existingPathMobile) data.append("ImagePathMobile", formData.existingPathMobile);

    try {
      if (id) {
        await api.put(`/banner/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banner güncellendi");
      } else {
        await api.post("/banner", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banner eklendi");
      }
      navigate("/banner-list");
    } catch {
      toast.error(id ? "Banner güncellenemedi" : "Banner eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const renderImageUpload = (label, icon, fileKey, previewKey) => (
    <div className="form-field">
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        {icon} {label}
      </label>
      <div className="image-upload-area">
        {formData[previewKey] ? (
          <div className="image-preview">
            <img src={formData[previewKey]} alt={label} />
            <button
              type="button"
              className="remove-image"
              onClick={() => clearFile(fileKey, previewKey)}
            >
              ×
            </button>
          </div>
        ) : (
          <label className="upload-placeholder">
            <ImagePlus size={32} />
            <span>{label} Yükle</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleFileChange(fileKey, previewKey, e.target.files[0])
              }
              hidden
            />
          </label>
        )}
      </div>
    </div>
  );

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Banner Düzenle" : "Banner Ekle"}</h2>
          <p className="page-subtitle">
            {id ? "Mevcut banner'ı düzenleyin" : "Yeni banner ekleyin"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="content-card" style={{ maxWidth: 700 }}>
          {renderImageUpload("Desktop Görseli", <Monitor size={18} />, "fileDesktop", "previewDesktop")}
          {renderImageUpload("Tablet Görseli", <Tablet size={18} />, "fileTablet", "previewTablet")}
          {renderImageUpload("Mobil Görseli", <Smartphone size={18} />, "fileMobile", "previewMobile")}

          <div className="form-field">
            <label>Banner Adı</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input"
              placeholder="Banner adı"
            />
          </div>

          <div className="form-field">
            <label>Sıralama</label>
            <input
              type="number"
              name="sortOrder"
              value={formData.sortOrder}
              onChange={handleChange}
              className="form-input"
              min="1"
            />
          </div>

          <div className="form-field">
            <label className="toggle-label">
              <span>Aktif</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={18} className="spin" /> Kaydediliyor...
              </>
            ) : (
              <>
                <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
